import {useEffect, useMemo, useState} from 'react';
import useYDocStore from '../../../stores/useYDocStore';
import {resolveDocumentAuthors} from '../../../utils/resolveDocumentAuthors';
import {decodeYDocToProsemirror} from '../../../utils/yjsSnapshot';
import {useReportStore} from '../useReportStore';

/**
 * Resolves author badges for preview from content attribution and version/report metadata.
 */
export default function usePreviewAuthors({
    content,
    versionDoc,
    meta,
    documentHeader,
    reportDoc,
    reportId,
    version,
    latestVersion,
}) {
    const previewDraft = useReportStore((s) => s.previewDraft);
    const [authors, setAuthors] = useState([]);
    const [loading, setLoading] = useState(false);

    const draftAuthors = useMemo(() => {
        if (
            !previewDraft?.authors?.length
            || previewDraft.reportId !== String(reportId)
            || version == null
            || latestVersion == null
            || String(version) !== String(latestVersion)
        ) {
            return null;
        }
        return previewDraft.authors;
    }, [previewDraft, reportId, version, latestVersion]);

    const contentForAuthors = useMemo(() => {
        if (content) return content;
        const ydoc = useYDocStore.getState().ydoc;
        if (!ydoc) return null;
        try {
            return decodeYDocToProsemirror(ydoc);
        } catch (_) {
            return null;
        }
    }, [content]);

    // Build author entries from the report document.
    // reportDoc.authorProfiles is a uid → {displayName, hasAvatar, profilePicture} map
    // pre-computed by the backend, so avatars and names render immediately without
    // a separate /api/users?ids=... call.
    const reportDocAuthors = useMemo(() => {
        const ownerUid = reportDoc?.userId || null;
        const ownerName = reportDoc?.userDisplayName || null;
        const profiles = reportDoc?.authorProfiles || {};

        const resolveEntry = (uid) => {
            const profile = profiles[uid];
            if (profile) {
                return {
                    uid,
                    displayName: profile.displayName || (uid === ownerUid ? ownerName : null),
                    hasAvatar: profile.hasAvatar === true,
                    profilePicture: profile.profilePicture || null,
                };
            }
            if (uid === ownerUid && ownerName) {
                return {uid, displayName: ownerName};
            }
            return uid;
        };

        if (Array.isArray(reportDoc?.authors) && reportDoc.authors.length > 0) {
            return reportDoc.authors.map(resolveEntry);
        }
        if (ownerUid) {
            return [resolveEntry(ownerUid)];
        }
        return [];
    }, [reportDoc]);

    useEffect(() => {
        if (draftAuthors?.length) {
            setAuthors(draftAuthors);
            setLoading(false);
            return undefined;
        }

        let cancelled = false;
        setLoading(true);

        const ownerProfile = reportDoc?.authorProfiles?.[reportDoc?.userId];
        const ownerFallback = reportDoc?.userId && reportDoc?.userDisplayName
            ? [{
                uid: reportDoc.userId,
                displayName: reportDoc.userDisplayName,
                hasAvatar: ownerProfile?.hasAvatar === true,
                profilePicture: ownerProfile?.profilePicture || null,
            }]
            : [];

        resolveDocumentAuthors({
            content: contentForAuthors,
            versionAuthors: versionDoc?.versionAuthors,
            metaAuthors: meta?.authors,
            headerAuthors: documentHeader?.authors,
            reportDocAuthors,
        })
            .then((resolved) => {
                if (!cancelled) {
                    // Drop entries that could not be resolved to a real name.
                    const withNames = (resolved || []).filter((a) => a.isAI || Boolean(a.displayName));
                    // If nothing resolved, fall back to the report owner so the field is never empty.
                    setAuthors(withNames.length > 0 ? withNames : ownerFallback);
                    setLoading(false);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setAuthors(ownerFallback);
                    setLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [contentForAuthors, versionDoc, meta, documentHeader, draftAuthors, reportDocAuthors]);

    return {authors, loading};
}
